// src/tabs/logements/InfoFbzTab.jsx — Phase 2.E.6 (ADR-039) + ADR-050
// Onglet Info FBZ (interne conciergerie). Renommé depuis InfoTab.jsx.
// Sections publiques (catégorie, capacité, tarifs, horaires schedule, règles)
// déplacées vers SyncBeds24Tab.jsx (anciennement AnnoncePubliqueTab) pour clarifier la distinction
// "ce que voit FBZ" vs. "ce que voient les voyageurs sur Airbnb/Booking".
//
// ADR-050 : la section "👤 Propriétaire" édite désormais cfg.props[propIdx].*
// directement (source de vérité unique partagée par TOUS les logements du
// proprio) au lieu de log.owner.* (sous-objet orphelin pré-ADR-050 dont la
// donnée n'était lue nulle part — confirmé par psql avant scrap).
//
// ⚠️ Backward compat: les anciens champs (adresse, codes, parking, etc.)
// restent éditables ici car utilisés par les templates Phase 2.B (cf. ADR-024).

import { useState, useEffect, useCallback } from "react";
import {
  Coins,
  Globe,
  FileText,
  Settings,
  Tag,
  User,
  CreditCard,
  RefreshCw,
} from "lucide-react";
import { DS } from "../../lib/ui";
import { propDisplayName } from "../../lib/format";
import { getBeds24Mapping } from "../../lib/beds24-mapping-lookup";
// WAGON R (R2bis) — import { uploadPhoto, deletePhotoStorage } de photos-api
// retiré avec la suppression de CoverPhotoSection (0 consommateur restant).
import {
  Row,
  Field,
  FieldFull,
  Inp,
  inpStyle,
} from "./atoms";
// DA (CHANTIER 65) — primitives « fiche logement » §8 ter (accordéons blanc
// franc + pastille teinte-catégorie + alertes lignes fines). Local au scope
// Logements (le SectionCard partagé d'atoms reste réservé au lot Livret).
import {
  FicheAccordion,
  FicheSubSection,
  FicheAlert,
  PrimaryButton,
  SecondaryButton,
} from "./_ficheUi";
import { getFacturationSource } from "../../lib/facturation-source";
// WAGON R (Lot R4 b) — LogementGalleryEditor déplacé vers LogementWebsiteTab
// (import retiré ici avec la section « 🖼️ Photos du logement »).
import DirectMultiplierEditor from "./DirectMultiplierEditor";
// CHANTIER OTA Lot O2 — bloc « Espace voyageur » (carte Administration) :
// suivre le réglage global OU personnaliser la matrice pour CE logement.
import { JourneyCompositionLogementSection } from "../../components/JourneyCompositionMatrix";
import OtaMultipliersReadOnly from "./OtaMultipliersReadOnly";
// WAGON R (dernier lot, A′ Dimitri 13/06) — min. séjours PAR CANAL Beds24
// read-only, sous le MinStayDefaultEditor (jamais d'écriture par canal).
import ChannelMinStayReadOnly from "./ChannelMinStayReadOnly";
// WAGON R (Lot R2) — réglages commerciaux migrés depuis SyncBeds24Tab :
// MinStayDefaultEditor (composant DB autonome, Wagon L) déplacé tel quel.
import MinStayDefaultEditor from "../../components/MinStayDefaultEditor";
// BasePriceEditor (composant DB autonome, Voie B+ — chemin argent Wagon L)
// porté tel quel depuis SyncBeds24Tab (WAGON R Lot R3 b, finding QA M2). Zéro
// modification interne, mêmes props que le Sync (logementId={log.id}).
import BasePriceEditor from "../../components/BasePriceEditor";
// MinPriceEditor (WAGON R5 lot F) — prix plancher BLOQUANT re-sourcé V2
// (logement_pricing_config.min_price), même mécanique DB autonome que BasePriceEditor.
import MinPriceEditor from "../../components/MinPriceEditor";
// LOT TAXE 0.4 (commit D) — bloc taxe de séjour (badge ⚠️ + rappel annuel Q6).
import TaxeReminderPanel from "./TaxeReminderPanel";
// D3 — miroir client de api/sites.js::resolveCautionFromLog (badge ⚠️
// « Montant caution à définir », carte Tarification — parité testée).
import { hasCautionAmount } from "../../lib/caution-helpers";
import {
  resolveCautionFromLog,
  cautionVoulueAZero,
} from "../../lib/caution-amount";
// WAGON R5 (A2) — useBasePrice retiré : plus d'aperçu prix OTA calculé.
// PLATFORMS / platformColor imports retirés Phase 6 PR1 (n'étaient
// utilisés que par le preview prix PriceLabs supprimé).
// PriceLabs imports retirés Phase 6 PR1 (ADR-090) — voir cleanup
// composants ci-dessous. src/lib/pricelabs.js conservé deprecated pour
// réutilisation PR2 healthcheck B-6.
import {
  getCandidatesForLog,
  isProviderOrphan,
} from "../../lib/menage-providers";
import { chargerRegles, poserRegle, retirerRegle } from "../../lib/menage-regles-api";
import { supabase } from "../../supabase";

// Constants DURATIONS, DEADLINE_HOURS, ROOMS retirées avec les sections
// voyageur-facing (PR 7 ADR-068).

const STATUSES = [
  { value: "active", label: "Actif" },
  { value: "inactive", label: "Inactif" },
  { value: "preparing", label: "En préparation" },
];

// WAGON R5 (B2/B4) — constantes TIMES / PERSONS retirées : les selects horaires
// et capacité ont déménagé vers LivretTab (zone Instructions). generateTimes /
// rangeOptions ne sont plus importés ici.

// WAGON R (Lot R2bis) — carte de section différenciée (UI pure).
// Structure STRICTEMENT identique aux 4 cartes (direction design Dimitri) :
// liseré gauche 3px en couleur d'accent + fond très léger, bordure fine sur
// les autres côtés, radius/padding repris du DS (cf. atom Section). Header =
// icône + titre (typo DS) + sous-titre 11-12px atténué quand fourni.
// FRONT 2 — SectionCard a été EXTRAIT vers ./atoms (mutualisé avec LivretTab,
// onglet Instructions en cartes). Les 4 cartes d'InfoFbzTab sont désormais
// repliables (collapsible/open/onToggle), cohérent avec l'onglet Instructions.

// État repliable des 4 cartes Info FBZ — persistant SESSION, défaut TOUT FERMÉ.
// Clé dédiée (distincte d'Instructions `fbz-livret-cards-open`).
const INFOFBZ_CARDS_KEY = "fbz-infofbz-cards-open";
const INFOFBZ_CARD_IDS = ["tarification", "direct", "facturation", "administration"];
function readInfoFbzCards() {
  // Défaut TOUT FERMÉ (décision Dimitri, cohérent Instructions) : titres +
  // sous-titres servent de sommaire, on ouvre ce qu'on veut.
  const allClosed = () =>
    Object.fromEntries(INFOFBZ_CARD_IDS.map((id) => [id, false]));
  try {
    const raw = sessionStorage.getItem(INFOFBZ_CARDS_KEY);
    if (!raw) return allClosed();
    const parsed = JSON.parse(raw);
    const base = allClosed();
    for (const id of INFOFBZ_CARD_IDS) {
      if (typeof parsed?.[id] === "boolean") base[id] = parsed[id];
    }
    return base;
  } catch (_) {
    return allClosed();
  }
}

export default function InfoFbzTab({
  log,
  prop,
  parentLog = null,
  isChambre = false,
  // LOT TAXE 0.4 (commit D) — cfg racine nécessaire au miroir Q1
  // (hasValidBareme : barème logement OU barème global legacy cfg.taxe_sejour).
  cfg = null,
  onUpdate,
  onUpdateProp,
  onUpdatePaliers,
  onAddPalier,
  onRemovePalier,
  onAddInvest,
  onRemoveInvest,
}) {
  const safeProp = prop || {};
  const propName = propDisplayName(safeProp);
  const nbLogsProp = (safeProp.logs || []).length;

  // Cartes repliables (cohérent avec l'onglet 📋 Instructions) — état persistant
  // SESSION global, défaut TOUT FERMÉ. Réutilise le SectionCard repliable
  // d'atoms.jsx (extrait au Front 2). Clé dédiée (≠ celle d'Instructions).
  const [openCards, setOpenCards] = useState(readInfoFbzCards);
  useEffect(() => {
    try {
      sessionStorage.setItem(INFOFBZ_CARDS_KEY, JSON.stringify(openCards));
    } catch (_) {}
  }, [openCards]);
  const toggleCard = useCallback((cardId) => {
    setOpenCards((prev) => ({ ...prev, [cardId]: prev[cardId] === false }));
  }, []);

  // WAGON R5 (A2) — l'aperçu prix OTA (previewBasePrice + useBasePrice) a été
  // retiré : le bloc OTA n'affiche plus de prix calculé (markup réel non exposé
  // par l'API Beds24). Le prix de base reste édité via BasePriceEditor (DB autonome).

  // WAGON R (Lot R2) — réglages commerciaux migrés depuis SyncBeds24Tab.
  // Helper d'écriture nested IDENTIQUE à updateNested du Sync (le path cfg et
  // le mécanisme onUpdate(parentKey, {...current, field: value}) sont inchangés
  // — déjà utilisé par le champ Caution ci-dessous).
  function updateNested(parentKey, field, value) {
    const current = log[parentKey] || {};
    onUpdate(parentKey, { ...current, [field]: value });
  }

  // WAGON R5 (B2/B4) — capacity + schedule (horaires check-in/out) et leur
  // logique d'héritage chambre ont déménagé vers LivretTab (zone Instructions,
  // section « 🏠 Caractéristiques du logement »). Variables retirées d'ici avec
  // la carte ⚙️ Logement supprimée.

  // Phase 4 PR 1 (ADR-076) : default_provider ménage par logement.
  // Source candidats = /api/admin-users (Supabase Auth + app_metadata.logIds[]).
  // PAS cfg.users (n'existe pas — découvert au test PR #127, fix amendement).
  // Auth requireAdmin côté API : seuls les admins voient cette UI (compte
  // ménage est forcé sur incidents/stock par App.jsx:363, n'arrive jamais ici).
  const [menageUsers, setMenageUsers] = useState([]);
  const [menageUsersLoading, setMenageUsersLoading] = useState(true);
  const [menageUsersError, setMenageUsersError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Session expirée");
        const res = await fetch("/api/admin-users", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { users } = await res.json();
        if (!cancelled) setMenageUsers(Array.isArray(users) ? users : []);
      } catch (e) {
        if (!cancelled) setMenageUsersError(e.message || String(e));
      } finally {
        if (!cancelled) setMenageUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const menageCandidates = getCandidatesForLog(log.id, menageUsers);

  // ── RÈGLE D'ASSIGNATION : la TABLE fait foi, plus `fbz8-cfg`. ──────────────
  // Ce déplacement est la CONDITION pour que l'admin et l'orchestrateur aient
  // des droits réellement égaux. Si cette fiche continuait d'écrire dans le KV
  // pendant que l'orchestrateur écrit dans la table, il faudrait arbitrer entre
  // deux sources — et la table gagnerait toujours. On aurait recréé une
  // hiérarchie sans le vouloir, exactement ce que Dimitri refuse.
  // `fbz8-cfg` n'est plus jamais écrit pour ce champ : ce KV est sur le chemin
  // Stripe, on n'y touche pas.
  const [regle, setRegle] = useState(null);
  const [regleErreur, setRegleErreur] = useState(null);
  const [regleEnCours, setRegleEnCours] = useState(false);

  const rechargerRegle = useCallback(async () => {
    const { regles } = await chargerRegles(supabase);
    setRegle(regles[log.id] || null);
  }, [log.id]);
  useEffect(() => { rechargerRegle(); }, [rechargerRegle]);

  const menageDefault = regle?.provider_email || "";
  const menageOrphan = isProviderOrphan(menageDefault, menageCandidates);

  const changerRegle = useCallback(async (email) => {
    setRegleEnCours(true); setRegleErreur(null);
    const r = email
      ? await poserRegle(supabase, log.id, email)
      : await retirerRegle(supabase, log.id);
    setRegleEnCours(false);
    if (!r.ok) { setRegleErreur(r.error); return; }
    await rechargerRegle();
  }, [log.id, rechargerRegle]);

  // Mapping Beds24 V2 (lecture seule — source de vérité)
  const [mapping, setMapping] = useState(null);
  const [mappingError, setMappingError] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setMappingLoading(true);
    setMappingError(null);
    getBeds24Mapping(log.id)
      .then((row) => {
        if (!cancelled) {
          setMapping(row);
          setMappingLoading(false);
        }
      })
      .catch((_e) => {
        if (!cancelled) {
          setMappingError(
            "Mapping Beds24 inaccessible. Réessaie ou recharge la page.",
          );
          setMappingLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [log.id]);

  return (
    <div>
      {/* WAGON R (Lot R2bis) — Section « 📸 Photo de couverture » SUPPRIMÉE
          (cartographie : champ cfg cover_photo/photos sans consommateur ni
          donnée prod).
          WAGON R (Lot R4 b) — Section « 🖼️ Photos du logement » (galerie V2
          LogementGalleryEditor) DÉPLACÉE vers l'onglet 🖥️ Website (contenu
          visuel du site, position logique en haut). Composant inchangé. */}

      {/* WAGON R5 (B4) — Sections « 🏷️ Identité interne » et « 👤 Propriétaire »
          DÉPLACÉES (moves byte-identiques, handlers intouchés) en bas de page dans
          la carte « ⚙️ Administration » (back-office et technique). */}

      {/* Section "💳 Stripe propriétaire" RETIRÉE de cette position
          (PR 8 ADR-069) — déplacée dans la carte « ⚙️ Administration » en bas
          de l'onglet (WAGON R5 R5h : en dernière sous-section). */}

      {/* PR 7 ADR-068 — sections voyageur-facing RETIRÉES de InfoFbz :
          - 📍 Adresse détaillée → onglet 📖 Livret (déjà avec héritage chambres)
          - 🚪 Accès bâtiment + appartement → onglet 📖 Livret
          - 🔑 Codes (porte/boîte/interphone/coffre/garage) → onglet 📖 Livret
            section "Codes d'accès"
          - 🔑 Wi-Fi (instructions + SSID + code legacy) → onglet 📖 Livret
            section "WiFi" (legacy retirés post PR #110 migration)
          - ⏱️ Slots opérationnels (checkin_duration / checkout_duration) →
            supprimés UI (0 consommateur — recon mega session). Data orpheline
            tolérée ADR-054.
          - 🏠 Composition logement (bedrooms / bathrooms / floor) → onglet
            📖 Livret section "Composition logement (contrat)"
          - 📍 quartier (public_address) → onglet 🖥️ Website (l'onglet Sync Beds24 a été supprimé, Wagon R R4)
          - ⏱️ booking_deadline → supprimé UI (0 consommateur)
          - 📞 Contact conciergerie override (contact_phone / contact_email)
            → supprimé UI (0 logement avec override non-vide en prod, audit
            psql mega session). Data lue par template-variables {fbz_phone}
            / {fbz_email} avec fallback cfg.co.tel / cfg.co.email.
          - 🏠 Infos pratiques (parking court + poubelles court) → supprimés
            (versions longues dans Livret).

          Cible PR 7 : Info FBZ admin/juridique uniquement — Identité interne,
          Propriétaire, Stripe, Business (avec invoice_prefix). Cf. ADR-068
          philosophie d'époque 3 onglets (l'onglet Sync Beds24 a été supprimé
          au Wagon R, ADR-195 — fiche actuelle : Info FBZ + Website). */}

      {/* Section "Horaires templates" RETIRÉE par PR 4bis (Q7 ADR-063) :
          unification vers `schedule.checkin_start` / `schedule.checkout_time`
          (édition désormais dans 📖 Livret > 📋 Instructions — R5 lot B). Les anciens champs `log.checkin_time`
          / `log.checkout_time` flat sont conservés en cfg pour rétrocompat
          (template-variables.js double fallback) mais ne sont plus éditables
          ici. Si valeurs flat existantes : elles restent valides tant que
          schedule.* est vide. */}

      {/* ═════════════════════════════════════════════════════════════════
          WAGON R (Lot R2bis) — restructuration VISUELLE en 4 cartes
          différenciées (direction design Dimitri, UI pure). Les sections
          « 💼 Business », « 🏷️ Réglages commerciaux » et « 💲 Multiplicateurs »
          sont dissoutes au profit de 4 SectionCard (Tarification générale /
          Réservations en direct / Facturation propriétaire / Logement). ZÉRO
          changement de logique : chaque champ garde son handler et son path cfg
          (onUpdate / onUpdateProp / updateNested), seul le regroupement visuel
          change. Liseré gauche d'accent (charte FBZ) + fond très léger par carte.
          ═══════════════════════════════════════════════════════════════ */}

      {/* ─── Carte 1 · Tarification générale (teinte-catégorie : ciel) ─── */}
      <FicheAccordion
        icon={Coins}
        tint="ciel"
        title="Tarification générale"
        subtitle="Sert à : prix public de toutes les plateformes (Beds24) · prix et durée min. de votre site direct · plancher de sécurité en saisie manuelle."
        open={openCards.tarification}
        onToggle={() => toggleCard("tarification")}
      >
        {/* Prix de base (mode manuel) — BasePriceEditor (composant DB autonome,
            Voie B+ / chemin argent Wagon L). Move byte-identique, mêmes props
            (logementId={log.id}). */}
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: DS.navy,
              marginBottom: 8,
            }}
          >
            Prix de base (mode manuel)
          </div>
          <BasePriceEditor logementId={log.id} />
        </div>

        {/* Restrictions séjour — MinStayDefaultEditor (composant DB autonome,
            Wagon L). Move byte-identique. */}
        <div
          style={{
            marginBottom: 14,
            paddingTop: 14,
            borderTop: "1px solid #eef1f5",
          }}
        >
          <MinStayDefaultEditor
            logementId={log.id}
            mode="section"
            onSaved={() => {
              // Coordination cross-UI : au prochain mount du calendrier le hook
              // useDailyPricing refetchera (donnée DB V2 pure). Cf. SyncBeds24Tab.
            }}
          />
          {/* Décision A′ Dimitri 13/06 (arbitrage min séjour) : le push écrit
              UNIQUEMENT room.minStay (le défaut) — les règles par canal Beds24
              sont préservées (smoke S-3 ADR-101). Le tunnel direct consomme ce
              défaut via daily_pricing (sync Beds24). */}
          <div style={{ fontSize: 11, color: "#667", marginTop: 4 }}>
            Écrit le min. séjour <strong>par défaut</strong> dans Beds24 — la
            valeur que votre site direct applique, et le repli des canaux sans
            règle propre. Les min. séjours par canal (Booking/Airbnb) gardent
            la main et restent gérés dans Beds24.
          </div>
          {/* WAGON R (A′ Dimitri 13/06) — affichage read-only des min. séjours
              PAR CANAL Beds24 (priceRule.minimumStay). Best-effort silencieux :
              ne rend rien si aucune règle ne porte de min. séjour ou si fetch KO. */}
          <ChannelMinStayReadOnly logementId={log.id} />
        </div>

        {/* WAGON R5 (A2) — markup par canal (Beds24 Channel Manager) DÉPLACÉ ici
            depuis « Réservations en direct » : même famille « visu plateformes »
            que le min. séjour par canal ci-dessus. Bloc honnête (plus de ×N ni de
            prix calculé : non exposés par l'API Beds24 — cf. composant). */}
        <div
          style={{
            marginBottom: 14,
            paddingTop: 14,
            borderTop: "1px solid #eef1f5",
          }}
        >
          <OtaMultipliersReadOnly logementId={log.id} />
        </div>

        {/* WAGON R5 lot F — prix plancher re-sourcé V2 (logement_pricing_config.
            min_price) via MinPriceEditor (DB autonome, comme BasePriceEditor).
            L'ancien champ KV (cfg.pricing.min_price) est abandonné côté code : la
            valeur KV reste en place (morte), le backfill F1 a porté lc1/lc3 en V2.
            Plancher BLOQUANT : la saisie manuelle calendrier refuse un prix plus
            bas (confirmation + raison pour passer outre). */}
        <div style={{ marginTop: 14 }}>
          <MinPriceEditor logementId={log.id} />
        </div>

        {/* LOT TAXE 0.4 (commit D) — taxe de séjour : badge ⚠️ « Barème taxe à
            valider » (Q1 strict, pousse vers la carte Adresse du Livret où vit
            la détection) + dead-man Q6 « dernier rappel » + bouton d'envoi
            manuel du rappel annuel. */}
        <TaxeReminderPanel log={log} cfg={cfg} />

        {/* D3 (GO Dimitri 2026-07-13) — badge ⚠️ « Montant caution à définir »
            (miroir client hasCautionAmount de resolveCautionFromLog serveur :
            self → parentLog, > 0). Même famille que le badge barème ci-dessus :
            sans montant, les devis publics répondent caution_non_definie
            (« bientôt disponible » côté site). Le montant s'édite dans la
            carte Administration → section Stripe. */}
        {/* Argent 1f : un 0 € VOULU n'est pas « à définir » (réservable sans caution). */}
        {!hasCautionAmount(parentLog ? { ...log, parentLog } : log) &&
          !cautionVoulueAZero(parentLog ? { ...log, parentLog } : log) && (
          <div
            style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}
            data-testid="caution-gate-panel"
          >
            <FicheAlert tone="warning" testId="caution-badge-a-definir">
              ⚠️ <strong>Montant caution à définir</strong> — sans montant, ce
              logement ne produit aucun devis Direct (« bientôt disponible »
              côté site). Rendez-vous dans la carte{" "}
              <strong>Administration → section Stripe</strong> : le champ
              « Montant caution (EUR) » s'y renseigne.
            </FicheAlert>
          </div>
        )}
      </FicheAccordion>

      {/* ─── Carte 2 · Réservations en direct (teinte-catégorie : indigo) ─── */}
      <FicheAccordion
        icon={Globe}
        tint="indigo"
        title="Réservations en direct"
        subtitle="Sert à : prix du site direct (multiplicateur × prix de base) · frais de ménage facturés au voyageur sur le site direct."
        open={openCards.direct}
        onToggle={() => toggleCard("direct")}
      >
        {/* WAGON R5 (A2) — le bloc OTA read-only (markup par canal) a été DÉPLACÉ
            dans la carte « 💰 Tarification générale » (visu plateformes). Ne reste
            ici que le multiplicateur Direct éditable (sous-loc lc1/lc2/lc3), qui
            concerne réellement les réservations en direct. */}
        {isChambre && <DirectMultiplierEditor logementId={log.id} />}
        {!isChambre && (
          <FicheAlert tone="info" style={{ marginTop: 14 }}>
            Le multiplicateur <strong>Direct</strong> n'est réglable que sur les
            sous-locations vendables en direct booking (lc1/lc2/lc3). Ce
            logement n'est pas une unité de vente directe concurrente.
          </FicheAlert>
        )}

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid #eef1f5",
          }}
        >
          <Row>
            {/* 🔴 ZONE ROUGE (chemin argent) — pricing.cleaning_fee = prix payé
                par le voyageur au tunnel direct. Champ DISTINCT de tarifMenage
                (refacturation proprio). NE JAMAIS câbler l'un sur l'autre. */}
            <Field label="Prix ménage — réservations en direct">
              <Inp
                type="number"
                value={log.pricing?.cleaning_fee ?? ""}
                onChange={(v) =>
                  updateNested("pricing", "cleaning_fee", parseFloat(v) || 0)
                }
                placeholder="ex: 50"
              />
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 4,
                  padding: "6px 10px",
                  background: "#f8fafd",
                  borderRadius: 6,
                  lineHeight: 1.4,
                }}
              >
                Facturé au voyageur sur le site direct. Le ménage Airbnb/Booking
                se gère directement sur les plateformes.
              </div>
            </Field>
            {/* WAGON R5 (A4 — unification caution) : le champ « 🛡️ Caution (€) »
                (pricing.deposit) a été SUPPRIMÉ ici. La caution est désormais
                saisie UNE seule fois via « Montant caution (EUR) » (caution_amount,
                section Stripe ci-dessous) — consommée par la pré-autorisation
                Stripe ET le contrat de location (rebranché sur caution_amount avec
                fallback legacy pricing.deposit ?? deposit, zéro contrat cassé). */}
          </Row>
        </div>
      </FicheAccordion>

      {/* ─── Carte 3 · Facturation propriétaire (teinte-catégorie : corail doux) ─── */}
      <FicheAccordion
        icon={FileText}
        tint="corail"
        title="Facturation propriétaire"
        subtitle="Sert à : factures et commissions propriétaire · planning et coût du ménage · numérotation des factures voyageur."
        open={openCards.facturation}
        onToggle={() => toggleCard("facturation")}
      >
        <FieldFull label="Mode de commission">
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[
              ["fixed", "Taux fixe"],
              ["degressive", "Dégressif"],
            ].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => onUpdate("commissionMode", mode)}
                style={{
                  flex: 1,
                  padding: "7px 10px",
                  border: `2px solid ${(log.commissionMode || "fixed") === mode ? DS.navy : "#dde3ec"}`,
                  borderRadius: 8,
                  background:
                    (log.commissionMode || "fixed") === mode
                      ? "#eef2f7"
                      : DS.white,
                  color:
                    (log.commissionMode || "fixed") === mode ? DS.navy : "#888",
                  fontWeight:
                    (log.commissionMode || "fixed") === mode ? 700 : 500,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {(log.commissionMode || "fixed") === "fixed" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 10px",
                background: "#f8fafd",
                borderRadius: 7,
              }}
            >
              <span style={{ fontSize: 12, color: "#555" }}>Taux :</span>
              <input
                type="number"
                min="0"
                max="100"
                value={log.taux || 0}
                onChange={(e) =>
                  onUpdate("taux", parseFloat(e.target.value) || 0)
                }
                style={{ ...inpStyle, width: 70 }}
              />
              <span style={{ fontSize: 12, color: "#888" }}>%</span>
            </div>
          )}
          {log.commissionMode === "degressive" && (
            <div
              style={{
                padding: "10px 12px",
                background: "#f8fafd",
                borderRadius: 7,
              }}
            >
              <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
                Paliers (du plus court au plus long) :
              </div>
              {(log.paliers || []).map((p, pi) => (
                <div
                  key={pi}
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 11, color: "#555" }}>≤</span>
                  <input
                    type="number"
                    value={p.maxNuits}
                    onChange={(e) =>
                      onUpdatePaliers(
                        pi,
                        "maxNuits",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    style={{ ...inpStyle, width: 55 }}
                  />
                  <span style={{ fontSize: 11, color: "#555" }}>nuits →</span>
                  <input
                    type="number"
                    value={p.taux}
                    onChange={(e) =>
                      onUpdatePaliers(
                        pi,
                        "taux",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    style={{ ...inpStyle, width: 55 }}
                  />
                  <span style={{ fontSize: 11, color: "#888" }}>%</span>
                  <button
                    onClick={() => onRemovePalier(pi)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: DS.red,
                      fontSize: 14,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                }}
              >
                <span style={{ fontSize: 11, color: "#555" }}>Au-delà :</span>
                <input
                  type="number"
                  value={log.tauxBase ?? log.taux ?? 0}
                  onChange={(e) =>
                    onUpdate("tauxBase", parseFloat(e.target.value) || 0)
                  }
                  style={{ ...inpStyle, width: 55 }}
                />
                <span style={{ fontSize: 11, color: "#888" }}>%</span>
              </div>
              <button
                onClick={onAddPalier}
                style={{
                  marginTop: 8,
                  padding: "5px 12px",
                  background: "#eef2f7",
                  color: DS.navy,
                  border: "none",
                  borderRadius: 7,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Palier
              </button>
            </div>
          )}
        </FieldFull>

        <Row>
          <Field label="Forfait ménage refacturé au propriétaire (€)">
            <Inp
              type="number"
              value={log.tarifMenage || ""}
              onChange={(v) => onUpdate("tarifMenage", parseFloat(v) || 0)}
              placeholder="0"
            />
          </Field>
          <Field label="Forfait société de ménage (€/intervention, optionnel)">
            <Inp
              type="number"
              value={log.menRealCostPerClean ?? ""}
              onChange={(v) =>
                onUpdate(
                  "menRealCostPerClean",
                  v === "" ? undefined : parseFloat(v) || 0,
                )
              }
              placeholder="—"
            />
          </Field>
        </Row>

        {/* WAGON M (Lot M3.1) — Source de facturation par logement.
            UI INERTE : le champ persiste (champ logement → updateLog →
            handleSave, même chemin que tarifMenage QUI MARCHE), mais AUCUN
            calcul ne le lit encore (câblage M3.2 gaté). Défaut 'kv' =
            zéro régression. Source de vérité = lib/facturation-source.js. */}
        <Row>
          <Field label="Source de facturation">
            <select
              value={getFacturationSource(log)}
              onChange={(e) => onUpdate("facturationSource", e.target.value)}
              style={inpStyle}
            >
              <option value="kv">V1 — imports CSV (défaut)</option>
              <option value="v2">V2 — sync Beds24 (auto)</option>
            </select>
            <div
              style={{
                fontSize: 11,
                color: "#666",
                marginTop: 4,
                padding: "6px 10px",
                background: "#f8fafd",
                borderRadius: 6,
                lineHeight: 1.4,
              }}
            >
              La bascule ne s'applique qu'aux mois non encore facturés. Les
              factures déjà émises restent inchangées.
            </div>
          </Field>
          {/* PR 7 ADR-068 — Q14 : préfixe factures multi-séquences (Phase 3
              ADR-066). Série voyageur-proprio (1 bailleur = 1 série).
              OBLIGATOIRE : si vide, l'émission des factures voyageur de ce
              proprio est désactivée (plus de fallback 'FRZ' commun). Les
              commissions FBZ utilisent la série 'P' (indépendante de ce champ).
              WAGON R (R2bis) : déplacé ici depuis 👤 Propriétaire (regroupement
              facturation). Handler onUpdateProp inchangé, scope cfg.props. */}
          <Field label="Préfixe factures (multi-séquences)">
            <Inp
              value={safeProp.invoice_prefix || ""}
              onChange={(v) =>
                onUpdateProp?.(
                  "invoice_prefix",
                  v.toUpperCase().replace(/[^A-Z0-9-]/g, ""),
                )
              }
              placeholder="ex: AVE / LAN — requis pour émettre"
            />
          </Field>
        </Row>

        {/* Phase 4 PR 1 (ADR-076) — Default provider ménage par logement.
            Source candidats = /api/admin-users (Supabase Auth + app_metadata.logIds[]).
            Override par résa = PR 3 (menage_runs.assigned_provider_id). */}
        <FieldFull label="Prestataire ménage assigné par défaut">
          <select
            data-testid="regle-menage-select"
            value={menageDefault}
            onChange={(e) => changerRegle(e.target.value)}
            disabled={menageUsersLoading || regleEnCours}
            style={{
              ...inpStyle,
              cursor: menageUsersLoading || regleEnCours ? "wait" : "pointer",
            }}
          >
            <option value="">
              {menageUsersLoading
                ? "Chargement comptes…"
                : "— Aucun (à assigner manuellement par résa) —"}
            </option>
            {/* Si l'email actuel n'est plus dans les candidats valides, on
                garde l'option en haut avec un préfixe d'alerte pour ne pas
                masquer la valeur stockée (comportement A silencieux). */}
            {menageOrphan && (
              <option value={menageDefault}>
                {menageDefault} (visibilité retirée)
              </option>
            )}
            {menageCandidates.map((c) => (
              <option key={c.id || c.email} value={c.email}>
                {c.email}
              </option>
            ))}
          </select>
          {/* QUI a posé la règle en cours, et QUAND. Ton orchestrateur écrit la
              MÊME ligne avec les mêmes droits : sans cette trace, chacun
              écraserait le travail de l'autre à l'aveugle. */}
          {regle && (
            <div
              data-testid="regle-menage-trace"
              style={{ marginTop: 6, fontSize: 11.5, color: "#5d6b78" }}
            >
              {(regle.posee_par || "").startsWith("migration:") ||
              regle.posee_par_role === "systeme"
                ? "Reprise automatique lors de la migration."
                : `Posée par ${regle.posee_par || "inconnu"}${
                    regle.posee_le
                      ? ` le ${regle.posee_le.slice(8, 10)}/${regle.posee_le.slice(5, 7)}/${regle.posee_le.slice(0, 4)}`
                      : ""
                  }`}
            </div>
          )}
          {regleErreur && (
            <FicheAlert tone="danger" style={{ marginTop: 6 }} data-testid="regle-menage-erreur">
              {regleErreur}
            </FicheAlert>
          )}
          {menageUsersError && (
            <FicheAlert tone="danger" style={{ marginTop: 6 }}>
              Erreur chargement comptes ménage : {menageUsersError}
            </FicheAlert>
          )}
          {menageOrphan && (
            <FicheAlert tone="warning" style={{ marginTop: 6 }}>
              Le compte <strong>{menageDefault}</strong> n'a plus accès à ce
              logement (visibilité retirée dans Paramètres &gt; Comptes). La
              valeur reste enregistrée mais ne sera pas appliquée tant qu'elle
              n'apparaît pas dans la liste candidats. Réassigne un compte valide
              ci-dessus.
            </FicheAlert>
          )}
          {!menageUsersLoading &&
            !menageDefault &&
            menageCandidates.length === 0 && (
              <FicheAlert tone="info" style={{ marginTop: 6 }}>
                Aucun compte ménage n'a accès à ce logement. Configure d'abord
                l'attribution dans <strong>Paramètres &gt; Comptes</strong>.
              </FicheAlert>
            )}
          {!menageUsersLoading &&
            !menageDefault &&
            menageCandidates.length > 0 && (
              <FicheAlert tone="info" style={{ marginTop: 6 }}>
                Sans default, chaque résa devra être assignée manuellement
                (Phase 4 PR 3 — onglet Ménage de la fiche résa).
              </FicheAlert>
            )}
        </FieldFull>

        {/* Seuil de ménage PAR LOGEMENT (décision Dimitri 2026-08-06 : « un
            studio et un T4 ne demandent pas le même temps »). Champ cfg ADDITIF
            `log.menage_duree_min` (KV/cfg_config, AUCUNE migration), défaut 90
            si vide. Lu côté serveur par lib/menage-duree.js (menage_log_info +
            écran orchestrateur « Demandes d'horaires »). */}
        <Field label="Durée de ménage minimum (minutes)">
          <Inp
            type="number"
            value={log.menage_duree_min ?? ""}
            onChange={(v) => {
              // Vide → undefined (retombe sur le défaut serveur 90) ; sinon
              // entier positif. Jamais de valeur négative persistée.
              const n = parseInt(v, 10);
              onUpdate(
                "menage_duree_min",
                Number.isFinite(n) && n > 0 ? n : undefined,
              );
            }}
            placeholder="90 (défaut)"
          />
          <div
            style={{
              fontSize: 11,
              color: "#666",
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            Temps minimum pour faire le ménage de ce logement — sert à l'écran
            Demandes d'horaires (fenêtre restante entre un départ tardif et
            l'arrivée suivante). Vide = 90 min.
          </div>
        </Field>

        {/* FieldFull "🔌 Mapping Beds24" RETIRÉ de Business (PR 8 ADR-069)
            — déplacé (WAGON R5 R5h) dans la sous-section « 🏷️ Identité interne »
            de la carte « ⚙️ Administration » (identité technique Beds24).
            « 🛡️ Caution (€) » (pricing.deposit) déplacée WAGON R R2bis vers la
            carte « 🌐 Réservations en direct ». */}

        <FieldFull label="Investissement initial">
          <InvestissementBuilder
            items={log.investissement || []}
            onAdd={onAddInvest}
            onRemove={onRemoveInvest}
          />
        </FieldFull>
      </FicheAccordion>

      {/* WAGON R5 (B2/B4) — Carte « ⚙️ Logement » (accent sauge) SUPPRIMÉE.
          Ses 2 derniers champs — capacité max (capacity.max_persons) et horaires
          check-in/check-out (schedule.checkin_start / schedule.checkout_time) —
          ont déménagé vers la zone Instructions (LivretTab, section « 🏠
          Caractéristiques du logement »), source unique des caractéristiques
          physiques du logement. Pur déménagement UI : paths cfg + héritage
          chambre sous-loc INCHANGÉS (consommateurs resolveLogTime / api/sites.js /
          contrat-html / templates inchangés). Le rappel « 📍 Adresse publique
          (déduite) » read-only avait déjà été retiré (R5 B1, décision Dimitri).
          Résultat : Info FBZ = 4 cartes (Tarification / Réservations / Facturation
          / Administration). */}

      {/* WAGON R (R2bis) — Section « 💲 Multiplicateurs par plateforme » dissoute :
          OtaMultipliersReadOnly + DirectMultiplierEditor déplacés dans la carte
          « 🌐 Réservations en direct » (regroupement visuel pur, mêmes props). */}

      {/* ─── Carte 5 · ⚙️ Administration (accent gris ardoise) ───
          WAGON R5 (B4) — regroupe en BAS de page le back-office et la technique
          (sans effet sur les voyageurs).
          WAGON R5 (R5h) — section « 🔌 Intégrations » SUPPRIMÉE (texte périmé :
          onglet Sync Beds24 retiré au R4, Stripe vivant ≠ « futur Phase 5 »,
          et PriceLabs se connecte à Beds24, jamais à l'app — aucun credential
          PriceLabs ici). Le bloc mapping Beds24 (lecture seule) a déménagé dans
          « 🏷️ Identité interne » (identité technique du logement chez Beds24).
          Ordre des 3 sous-sections : Identité interne (+ mapping) / Propriétaire
          / Stripe. Moves byte-identiques des blocs existants (handlers intouchés,
          y compris onUpdateProp scope proprio). */}
      <FicheAccordion
        icon={Settings}
        tint="ardoise"
        title="Administration"
        subtitle="Sert à : identité interne et variables messages ({logement_name}) · mapping Beds24 · facturation propriétaire · pré-autorisation Stripe (caution, tous canaux)."
        open={openCards.administration}
        onToggle={() => toggleCard("administration")}
      >
        {/* ═════ Section 🏷️ Identité interne ═════
            WAGON R5 (B4) — déplacée ici (move byte-identique, handlers intouchés).
            WAGON R5 (R5h) — accueille désormais le mapping Beds24 (lecture seule,
            même famille « identité technique du logement chez Beds24 »). */}
        <FicheSubSection
          icon={Tag}
          first
          title="Identité interne"
          intro="Informations utilisées en interne par FBZ. Pour la fiche publique (titre, description, capacité, tarifs, règles), voir l'onglet Website."
        >
          <Row>
            <Field label="ID (lecture seule)">
              <Inp value={log.id} disabled />
            </Field>
            <Field label="Statut">
              <select
                value={log.status || "active"}
                onChange={(e) => onUpdate("status", e.target.value)}
                style={inpStyle}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Nom court (variable {logement_name})">
              <Inp
                value={log.nom || ""}
                onChange={(v) => onUpdate("nom", v)}
                placeholder="ex: Cocon Naturel"
              />
            </Field>
            <Field label="Nom complet (variable {logement_full_name})">
              <Inp
                value={log.nom_complet || ""}
                onChange={(v) => onUpdate("nom_complet", v)}
                placeholder={`ex: ${log.nom || "Cocon Naturel"} — adresse`}
              />
            </Field>
          </Row>
          {/* WAGON R5 (R5h) — Mapping Beds24 (lecture seule, source = Paramètres
              → 🔌 Beds24) déplacé depuis l'ex-section « 🔌 Intégrations » : c'est
              l'identité technique du logement chez Beds24. Comportement identique. */}
          <FieldFull label="Beds24 — Mapping property/room (lecture seule)">
            <div
              style={{
                padding: "10px 12px",
                background: "#f8fafd",
                borderRadius: 8,
                fontSize: 12,
                color: "#444",
                marginBottom: 10,
              }}
            >
              {mappingLoading && <span>Chargement…</span>}
              {mappingError && (
                <span style={{ color: "#c00" }}> {mappingError}</span>
              )}
              {!mappingLoading && !mappingError && mapping && (
                <span>
                  Property <strong>{mapping.beds24_property_id}</strong>
                  {mapping.beds24_room_id && (
                    <>
                      {" "}
                      · Room <strong>{mapping.beds24_room_id}</strong>
                    </>
                  )}
                  {mapping.property_name && <> · {mapping.property_name}</>}
                </span>
              )}
              {!mappingLoading && !mappingError && !mapping && (
                <span>Pas de mapping configuré</span>
              )}
              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                Configurer dans <strong>Paramètres → Beds24</strong>. Édition
                centralisée admin global, pas par logement.
              </div>
            </div>
          </FieldFull>
        </FicheSubSection>

        {/* ═════ Sous-section Propriétaire (source unique cfg.props[propIdx]) ═════
            WAGON R5 (B4) — déplacée ici (move byte-identique, handlers intouchés). */}
        <FicheSubSection
          icon={User}
          title="Propriétaire"
          intro={`Ces informations sont partagées par TOUS les logements de ${propName || "(propriétaire sans nom)"} (${nbLogsProp} logement${nbLogsProp > 1 ? "s" : ""}). Toute modification ici se répercute partout.`}
        >
          {/* Un propriétaire personne physique a un nom de famille + un prénom
              distincts (cas courant). La sous-location FBZ, elle, porte un nom
              unique (« FULLBNBZEN (sous-location) ») sans séparation nom/prénom :
              dans ce cas on affiche un seul champ pré-rempli avec ce nom, plutôt
              que deux champs vides. On ne fabrique jamais une séparation
              artificielle à partir d'un nom complet. */}
          {safeProp.nom_famille || safeProp.prenom ? (
            <Row>
              <Field label="Nom de famille">
                <Inp
                  value={safeProp.nom_famille || ""}
                  onChange={(v) => onUpdateProp?.("nom_famille", v)}
                  placeholder="ex: AVELAR"
                />
              </Field>
              <Field label="Prénom">
                <Inp
                  value={safeProp.prenom || ""}
                  onChange={(v) => onUpdateProp?.("prenom", v)}
                  placeholder="ex: Bruno"
                />
              </Field>
            </Row>
          ) : (
            <FieldFull label="Nom">
              <Inp
                value={safeProp.nom || ""}
                onChange={(v) => onUpdateProp?.("nom", v)}
                placeholder="ex: FULLBNBZEN (sous-location)"
              />
            </FieldFull>
          )}
          <Row>
            <Field label="Email">
              <Inp
                type="email"
                value={safeProp.email || ""}
                onChange={(v) => onUpdateProp?.("email", v)}
                placeholder="ex: bruno@example.fr"
              />
            </Field>
            <Field label="Téléphone">
              <Inp
                type="tel"
                value={safeProp.telephone || ""}
                onChange={(v) => onUpdateProp?.("telephone", v)}
                placeholder="ex: +33 6 12 34 56 78"
              />
            </Field>
          </Row>
          {/* adresse FACTURATION proprio (granularité proprio) — distincte de l'adresse voyageur du Livret, NE PAS fusionner (BUG 15) */}
          <FieldFull label="Adresse (facturation propriétaire)">
            <Inp
              value={safeProp.adresse || ""}
              onChange={(v) => onUpdateProp?.("adresse", v)}
              placeholder="ex: 12 rue d'Alsace"
            />
          </FieldFull>
          <Row>
            <Field label="Code postal">
              <Inp
                value={safeProp.code_postal || ""}
                onChange={(v) => onUpdateProp?.("code_postal", v)}
                placeholder="ex: 31000"
              />
            </Field>
            <Field label="Ville">
              <Inp
                value={safeProp.ville || ""}
                onChange={(v) => onUpdateProp?.("ville", v)}
                placeholder="ex: Toulouse"
              />
            </Field>
          </Row>
          {/* WAGON R (Lot R2bis) — « Préfixe factures » déplacé (regroupement
              visuel pur) vers la carte « Facturation propriétaire ». Handler
              inchangé (onUpdateProp, scope cfg.props[propIdx].invoice_prefix). */}
        </FicheSubSection>

        {/* ═════ Sous-section Stripe propriétaire ═════
            WAGON R5 (R5h) — placée en dernier dans la carte Administration
            (ordre voulu : Identité interne / Propriétaire / Stripe). Sous-section
            autonome (PriceLabs UI retirée Phase 6 PR1 ADR-090 ; PRICELABS_API_KEY
            env var Vercel unique). Handlers intouchés. */}
        <StripeSection log={log} onUpdate={onUpdate} parentLog={parentLog} />

        {/* ═════ Sous-section Espace voyageur (CHANTIER OTA Lot O2) ═════
            Composition des blocs de l'espace voyageur (formulaire / caution /
            livret / reçu + état des lieux, future section du livret) pour CE
            logement : suivre le réglage global (Paramètres → Espace voyageur,
            défaut) OU personnaliser (exception
            rare, même matrice 3 plateformes × 5 blocs). Persistance DB
            autonome (journey_composition_config via beds24-mapping) — hors
            cfg, hors dirty-guard de la fiche. */}
        <FicheSubSection
          icon={Globe}
          title="Espace voyageur"
          intro="Composition des blocs voyageur pour ce logement — suit le réglage global par défaut ; personnalisation = exception rare."
        >
          <JourneyCompositionLogementSection logementId={log.id} />
        </FicheSubSection>
      </FicheAccordion>
    </div>
  );
}

// WAGON R (Lot R2bis) — Composant CoverPhotoSection SUPPRIMÉ.
// La section « 📸 Photo de couverture » (champ cfg cover_photo / photos)
// n'avait aucun consommateur ni donnée prod (cartographie validée).
// Helpers exclusifs (preview blob URL.createObjectURL, upload/delete
// Storage) retirés avec lui. Ne PAS confondre avec la galerie V2
// (LogementGalleryEditor) — conservée, et déplacée vers l'onglet Website (R4).

// ── Sous-section PriceLabs RETIRÉE Phase 6 PR1 (ADR-090) ─────────────
// Composants PriceLabsSection / PriceLabsConnectForm / PriceLabsConnectedView
// supprimés. Saisie API key par logement remplacée par env var Vercel
// PRICELABS_API_KEY unique (Phase 6 PR2 : endpoint serveur consommera
// la clé via process.env, pas saisie UI). Cf. ADR-090.
//
// V1 KV cfg.props[].logs[].pricelabs.* reste DORMANT en lecture pour
// rétrocompat. Scrub Phase 11.

// ── Sous-section Stripe Phase 2 (saisie stripe_account_id + caution_amount) ──
//
// Commit C11 PR #189 — refactor UI legacy V1 → UI Phase 2 :
//   - Suppression bouton legacy "Connecter compte Stripe" + modal V1
//     (publicKey/secretKey/webhookSecret) → mode V1 archive.
//   - Nouvelle UI Phase 2 : saisie directe `log.stripe_account_id`
//     (format `acct_xxx`) — sous-compte Stripe Connect Standard du
//     proprio, créé via Dashboard Stripe Connect.
//   - `log.stripe` legacy KV reste DORMANT en lecture rétrocompat
//     (pas de scrub DB).
//   - Pas de bouton "Tester connexion" : différé post-merge (test via
//     simple résa direct + flux paiement Stripe).
//
// Commit C14 PR #189 — fix régression intra-PR smoke prod #3 :
//   - Ajout saisie UI `log.caution_amount` (montant pré-autorisé Stripe EUR).
//   - Endpoint `api/stripe.js create_caution` retourne 400 `caution_amount_missing`
//     si non saisi → bloquant flux paiement. Helper `getCautionAmount`
//     (lib/cfg-reader-server.js) lit `cfg.props[].logs[].caution_amount`
//     avec fallback parent sous-loc.
//   - Validation côté front : valeur > 0 obligatoire, warning conseillé
//     200-500 EUR (< 50 ou > 2000 = warning advisory).
//   - Chambres Maison Zen : depuis ADR-197 LOT 2, le parent l8 n'est plus
//     éditable via l'UI (ligne de regroupement non cliquable). On saisit le
//     `caution_amount` ET le `stripe_account_id` DIRECTEMENT sur chaque chambre
//     lc1/lc2/lc3 (elles ont leurs valeurs propres : caution=50, acct_… own).
//
// Note : le fallback parent reste lu CÔTÉ SERVEUR (cfg-reader-server) pour
// rétrocompat, mais le point de saisie UI est désormais la chambre, pas l8.
// Le résolveur hérite encore du parent si une chambre n'a pas sa valeur.

// Argent 1f — décision de Dimitri du 29/10 : la caution se saisit en EUROS
// ENTIERS, sans centimes (comme la maquette) : « 300 », « 0 ». Tout le reste
// (« 300,50 », « 300.5 », « 0,00 », « 1.000 », « 1 000 », « 1e3 »…) → NaN
// (saisie refusée). Le signe moins est lu pour pouvoir dire « négatif »
// (refusé ensuite).
export function lireSaisieCaution(texte) {
  const t = String(texte ?? "").trim();
  return /^-?\d+$/.test(t) ? Number(t) : NaN;
}

// Décision de Dimitri du 29/10 : avertissements « montant inhabituel » et
// confirmations de la caution en AUBERGINE (plus d'encadré ambre).
const AUBERGINE = "#7A2E6E";
const STYLE_ALERTE_AUBERGINE = {
  background: "#F4E7F1",
  border: "0.5px solid #DDC2D7",
  borderLeft: `3px solid ${AUBERGINE}`,
  color: "#5E2255",
  marginBottom: 8,
};

export function StripeSection({ log, onUpdate, parentLog = null }) {
  const [editing, setEditing] = useState(false);
  const [accountInput, setAccountInput] = useState(log.stripe_account_id || "");
  const [accountError, setAccountError] = useState(null);

  // Caution amount state (C14)
  const [cautionInput, setCautionInput] = useState(
    log.caution_amount != null ? String(log.caution_amount) : "",
  );
  const [cautionError, setCautionError] = useState(null);
  // Argent 1f (relecture) : 0 € = « pas de caution » — confirmé EN LIGNE
  // avant d'enregistrer, pour qu'un 0 tapé par erreur ne passe pas en silence.
  // null | "zero" (0 € = pas de caution) | "vide" (revenir à la caution du
  // parent) | "bas" (moins de 50 €) — même confirmation en ligne.
  const [confirmation, setConfirmation] = useState(null);

  // Format Stripe Connect officiel : `acct_` + 1+ chars alphanumériques.
  const accountRegex = /^acct_[a-zA-Z0-9]+$/;

  const hasAgencyAccountId =
    !!log.stripe_account_id &&
    typeof log.stripe_account_id === "string" &&
    accountRegex.test(log.stripe_account_id);

  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 FAUSSE ALERTE « Montant caution non configuré » — corrigé le 2026-08-05
  // ═══════════════════════════════════════════════════════════════════════
  // Il y avait ICI une constante locale `hasCautionAmount` qui MASQUAIT
  // (shadowing) le helper importé en haut du fichier. Elle exigeait
  // `typeof log.caution_amount === "number"`, là où le helper — miroir du
  // résolveur serveur `resolveCautionFromLog` — fait un `Number(...)`.
  //
  // Or en PRODUCTION les montants ne sont pas tous du même type :
  //   lc1 « Cocon Naturel » → 50   (nombre)  ✅
  //   lc2 « Soleil du Sud » → "50" (CHAÎNE)  ❌ vu comme non configuré
  //   lc3 « Sur un Nuage »  → "50" (CHAÎNE)  ❌ vu comme non configuré
  //
  // Conséquence : sur 2 chambres sur 3, l'écran affichait un bandeau ROUGE
  // « cautions bloquées, aucun devis Direct » et grisait les deux cases
  // « Création automatique du lien de caution » — alors que la caution
  // fonctionnait parfaitement, le serveur résolvant bien 50 €. Un réglage
  // opérationnel paraissait cassé, et ses interrupteurs étaient inutilisables.
  //
  // Le même fichier utilisait DÉJÀ correctement le helper importé pour le
  // badge de la carte Tarification — d'où deux verdicts opposés sur le même
  // logement, dans le même écran.
  //
  // CORRECTIF : suppression du doublon local. Une seule règle, celle qui fait
  // foi côté serveur (self, puis logement parent pour les chambres sous-loc).
  const cautionConfigured = hasCautionAmount(
    parentLog ? { ...log, parentLog } : log,
  );
  // Argent 1f (Dimitri 07/10) : 0 € VOULU = « pas de caution » — réglage
  // valable, ni alerte « non configuré » ni « caution impossible ».
  const cautionSansCaution = cautionVoulueAZero(
    parentLog ? { ...log, parentLog } : log,
  );

  // Advisory warning : valeur saisie hors plage recommandée (50-2000).
  // Number(...) explicite : « 50 » (chaîne) doit être comparé comme un montant,
  // pas comme du texte — même cause que le bug ci-dessus.
  const cautionAdvisoryWarning = (() => {
    if (!cautionConfigured) return null;
    // Règle unique (07/10) : 0 = pas de caution, vide = celle du parent.
    const montant = resolveCautionFromLog(parentLog ? { ...log, parentLog } : log);
    if (montant == null) return null;
    if (montant < 50)
      return "Montant inhabituellement bas (< 50 EUR). Recommandé 200-500 EUR.";
    if (montant > 2000)
      return "Montant inhabituellement élevé (> 2000 EUR). Vérifier la saisie.";
    return null;
  })();

  // Champ vidé = retour au « réglage général du logement » : ce que la règle
  // unique (resolveCautionFromLog) donne sans valeur propre — celle du
  // logement parent pour une chambre ; sinon, rien de défini.
  const logSiVide = { ...log, caution_amount: null, parentLog: parentLog || null };
  const cautionSiVide = resolveCautionFromLog(logSiVide);
  const zeroSiVide = cautionVoulueAZero(logSiVide);

  function saveCaution(confirme = false) {
    const trimmed = cautionInput.trim();
    if (!trimmed) {
      // Argent 1f (relecture) : vider le champ = revenir à la caution du
      // parent — action explicite, confirmée en ligne avant d'effacer.
      setCautionError(null);
      if (!confirme) {
        setConfirmation("vide");
        return;
      }
      setConfirmation(null);
      onUpdate("caution_amount", null);
      return;
    }
    const num = lireSaisieCaution(trimmed);
    if (!Number.isFinite(num) || Number.isNaN(num)) {
      setCautionError(
        "Valeur invalide : saisir un nombre entier en euros",
      );
      return;
    }
    // Argent 1f (Dimitri 07/10) : 0 = pas de caution (accepté) ; négatif =
    // erreur (refusé, comme POST /api/cfg).
    if (num < 0) {
      setCautionError(
        "Le montant ne peut pas être négatif (0 = pas de caution ; recommandé 200-500 EUR).",
      );
      return;
    }
    // 0 (pas de caution), moins de 50 € ou plus de 2000 € : confirmation en
    // ligne d'abord (une faute de frappe « 3000 » pour « 300 » ne passe pas).
    if (!confirme && (num === 0 || num < 50 || num > 2000)) {
      setCautionError(null);
      setConfirmation(num === 0 ? "zero" : num < 50 ? "bas" : "haut");
      return;
    }
    setConfirmation(null);
    onUpdate("caution_amount", num);
    setCautionError(null);
  }

  function saveAccount() {
    const trimmed = accountInput.trim();
    if (!trimmed) {
      onUpdate("stripe_account_id", null);
      setAccountError(null);
      setEditing(false);
      return;
    }
    if (!accountRegex.test(trimmed)) {
      setAccountError(
        "Format invalide. Attendu : acct_xxx (lettres + chiffres uniquement)",
      );
      return;
    }
    onUpdate("stripe_account_id", trimmed);
    setAccountError(null);
    setEditing(false);
  }

  function cancelEdit() {
    setAccountInput(log.stripe_account_id || "");
    setAccountError(null);
    setEditing(false);
  }

  function disconnect() {
    if (
      !window.confirm(
        "Déconnecter le sous-compte Stripe ? La référence acct_xxx sera effacée du logement.",
      )
    )
      return;
    onUpdate("stripe_account_id", null);
    setAccountInput("");
    setAccountError(null);
    setEditing(false);
  }

  return (
    <FicheSubSection icon={CreditCard} title="Stripe propriétaire (sous-compte)">
      {/* ── Bloc montant caution (C14) — saisi AVANT stripe_account_id ── */}
      {!cautionConfigured && !cautionSansCaution && (
        <FicheAlert
          tone="danger"
          testId="caution-amount-not-configured-warning"
          style={{ marginBottom: 12 }}
        >
          <strong>Montant caution non configuré</strong> pour ce logement. Les
          cautions Stripe seront <strong>bloquées</strong> et le site direct ne
          produit <strong>aucun devis ni demande de réservation</strong>
          {" "}(« bientôt disponible » côté site) tant que le champ{" "}
          <code>caution_amount</code> n'est pas renseigné ci-dessous (montant en
          EUR — à défaut, pour une chambre Maison Zen, le montant du logement
          parent est hérité côté serveur).
        </FicheAlert>
      )}

      <FieldFull label="Montant caution (EUR)">
        {/* Argent 1f (relecture) : champ TEXTE (clavier décimal) — un champ
            numérique du navigateur rend un texte vide sur une saisie
            invalide, ce qui effaçait la caution en silence. Local : le
            composant partagé Inp sert à d'autres champs. */}
        <input
          type="text"
          inputMode="decimal"
          data-testid="caution-amount-input"
          value={cautionInput}
          onChange={(e) => {
            setCautionInput(e.target.value);
            setConfirmation(null);
          }}
          placeholder="300"
          style={{ ...inpStyle, background: "#f8fafd", color: "#444" }}
        />
        {/* Règle Dimitri 13/06 (champs à portée multiple) : micro-mention de TOUS
            les consommateurs. WAGON R5 (A4) — champ caution UNIFIÉ : ce montant
            alimente la pré-autorisation Stripe (tous canaux) ET le contrat de
            location (rebranché sur caution_amount). */}
        <div style={{ fontSize: 11, color: DS.textSecondary, marginTop: 4 }}>
          Utilisé par : <strong>pré-autorisation Stripe</strong> (site direct +
          livret OTA) + <strong>contrat de location</strong>.
        </div>
      </FieldFull>
      {cautionError && (
        <FicheAlert tone="danger" testId="caution-amount-input-error" style={{ marginBottom: 8 }}>
          {cautionError}
        </FicheAlert>
      )}
      {/* Argent 1f : texte permanent quand la caution réglée vaut 0 €. */}
      {cautionSansCaution && !confirmation && (
        <div data-testid="caution-amount-zero-texte" style={{ fontSize: 11, color: DS.textSecondary, marginBottom: 8 }}>
          Pas de caution (0 €) — le site direct réserve sans caution.
        </div>
      )}
      {confirmation && (
        <FicheAlert tone="info" testId={`caution-amount-${confirmation}-confirm`} style={STYLE_ALERTE_AUBERGINE}>
          {confirmation === "zero" && (
            <>0 € = <strong>pas de caution</strong> : le site direct réservera ce logement sans caution.{" "}</>
          )}
          {confirmation === "vide" && (
            // Ce qui se passera VRAIMENT, champ vidé (même règle que le serveur).
            <>
              Champ vide = retour au réglage général du logement :{" "}
              <strong>
                {cautionSiVide != null
                  ? `${Number(cautionSiVide).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                  : zeroSiVide
                    ? "pas de caution (0 €)"
                    : "caution non définie, réservation directe bloquée"}
              </strong>
              .{" "}
            </>
          )}
          {confirmation === "bas" && (
            <>Moins de 50 € : <strong>montant inhabituellement bas</strong>.{" "}</>
          )}
          {confirmation === "haut" && (
            <>Plus de 2000 € : <strong>montant inhabituellement élevé</strong>, vérifier la saisie.{" "}</>
          )}
          <PrimaryButton onClick={() => saveCaution(true)} style={{ background: AUBERGINE }}>
            {confirmation === "zero"
              ? "Confirmer : pas de caution"
              : confirmation === "vide"
                ? cautionSiVide != null || zeroSiVide
                  ? "Confirmer : réglage général du logement"
                  : "Confirmer : vider le champ"
                : "Confirmer ce montant"}
          </PrimaryButton>
        </FicheAlert>
      )}
      {cautionAdvisoryWarning && (
        <FicheAlert tone="info" testId="caution-amount-advisory-warning" style={STYLE_ALERTE_AUBERGINE}>
          {cautionAdvisoryWarning}
        </FicheAlert>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <PrimaryButton onClick={() => saveCaution()}>Sauvegarder caution</PrimaryButton>
      </div>
      <div style={helperBoxStyle}>
        Montant pré-autorisé Stripe (hold) en cas de dégâts. Recommandé{" "}
        <strong>200-500 EUR</strong> selon logement.{" "}
        <strong>Chambres Maison Zen</strong> (Cocon Naturel, Soleil du Sud, Sur
        un Nuage) : la caution se saisit désormais{" "}
        <strong>directement sur chaque chambre</strong>, qui a sa propre valeur.
      </div>

      {/* Bandeau warning Phase 2 (ADR-108) si stripe_account_id absent/invalide */}
      {!hasAgencyAccountId && (
        <FicheAlert
          tone="danger"
          testId="stripe-account-not-configured-warning"
          style={{ marginTop: 16, marginBottom: 12 }}
        >
          <strong>Sous-compte Stripe non configuré</strong> pour ce logement.
          Les paiements et cautions Stripe seront <strong>bloqués</strong> tant
          que le champ <code>stripe_account_id</code> (format{" "}
          <code>acct_xxx</code>) n'est pas renseigné ci-dessous.
        </FicheAlert>
      )}

      {hasAgencyAccountId && !editing && (
        <div style={{ marginTop: 16 }}>
          <FicheAlert tone="success" style={{ marginBottom: 12 }}>
            <strong>Compte Stripe connecté :</strong>{" "}
            <code style={{ fontFamily: "monospace" }}>{log.stripe_account_id}</code>
          </FicheAlert>
          <div style={{ display: "flex", gap: 6 }}>
            <SecondaryButton onClick={() => setEditing(true)}>Modifier</SecondaryButton>
            <SecondaryButton tone="danger" onClick={disconnect}>Déconnecter</SecondaryButton>
          </div>
        </div>
      )}

      {(!hasAgencyAccountId || editing) && (
        <div style={{ marginTop: 16 }}>
          <FieldFull label="Identifiant sous-compte Stripe Connect (acct_xxx)">
            <Inp
              value={accountInput}
              onChange={setAccountInput}
              placeholder="acct_1A2B3C4D5E..."
            />
          </FieldFull>
          {accountError && (
            <FicheAlert tone="danger" testId="stripe-account-input-error" style={{ marginBottom: 8 }}>
              {accountError}
            </FicheAlert>
          )}
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <PrimaryButton onClick={saveAccount}>Sauvegarder</PrimaryButton>
            {editing && (
              <SecondaryButton onClick={cancelEdit}>Annuler</SecondaryButton>
            )}
          </div>
        </div>
      )}

      <div style={helperBoxStyle}>
        Saisir le sous-compte Stripe Connect du proprio (format{" "}
        <code>acct_xxx</code>, créé via Dashboard Stripe Connect).{" "}
        <strong>Chambres Maison Zen</strong> (Cocon Naturel, Soleil du Sud, Sur
        un Nuage) : le compte Stripe se saisit désormais{" "}
        <strong>directement sur chaque chambre</strong>, qui a son propre{" "}
        <code>stripe_account_id</code>.
      </div>

      {/* ── Création auto du lien de caution par plateforme (ADR-174) ── */}
      <div
        data-testid="caution-auto-platforms"
        style={{ marginTop: 16, borderTop: `1px solid ${DS.border}`, paddingTop: 14 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            fontFamily: DS.fontDisplay,
            fontSize: 14,
            fontWeight: 600,
            color: DS.marineDark,
            marginBottom: 8,
          }}
        >
          <RefreshCw size={15} strokeWidth={1.8} style={{ color: DS.marine }} />{" "}
          Création automatique du lien de caution
        </div>
        {["direct", "booking"].map((key) => {
          const platforms =
            log.caution_auto_platforms &&
            typeof log.caution_auto_platforms === "object"
              ? log.caution_auto_platforms
              : { direct: true, booking: true };
          const labels = {
            direct: "Caution sur réservations Direct",
            booking: "Caution sur réservations Booking",
          };
          const configReady = cautionConfigured && hasAgencyAccountId;
          return (
            <label
              key={key}
              data-testid={`caution-platform-${key}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minHeight: 44,
                cursor: configReady ? "pointer" : "not-allowed",
                opacity: configReady ? 1 : 0.5,
              }}
            >
              <input
                type="checkbox"
                checked={platforms[key] !== false}
                disabled={!configReady}
                onChange={(e) =>
                  onUpdate("caution_auto_platforms", {
                    ...platforms,
                    [key]: e.target.checked,
                  })
                }
                style={{ width: 18, height: 18, cursor: "inherit" }}
              />
              <span style={{ fontSize: 13, color: DS.marineDeep }}>{labels[key]}</span>
            </label>
          );
        })}
        {/* Argent 1f : 0 € voulu → pas de caution, rien à « renseigner d'abord ». */}
        {cautionSansCaution ? null : !cautionConfigured || !hasAgencyAccountId ? (
          <FicheAlert tone="warning" style={{ marginTop: 8 }}>
            Renseigne d'abord le <strong>montant caution</strong> et le{" "}
            <strong>sous-compte Stripe</strong> ci-dessus pour activer ces
            options.
          </FicheAlert>
        ) : (
          <div style={{ ...helperBoxStyle, marginTop: 8, marginBottom: 0 }}>
            FBZ crée automatiquement le lien de caution pour les réservations
            des plateformes cochées (Airbnb non concerné — caution gérée par
            Airbnb). La création effective reste gouvernée par le flag de mise
            en service <code>CAUTION_AUTOLINK_LIVE</code> (dormant tant qu'il
            n'est pas activé côté serveur).
          </div>
        )}
      </div>
    </FicheSubSection>
  );
}

// Encart d'aide « calme » (secondaire) — surface neutre en retrait, jamais une
// alerte (DESIGN : le corail/ambre est réservé à ce qui demande une action).
const helperBoxStyle = {
  padding: "9px 12px",
  background: DS.pageBg,
  border: `0.5px solid ${DS.border}`,
  borderRadius: 8,
  fontSize: 11.5,
  color: DS.textSecondary,
  lineHeight: 1.5,
  marginBottom: 16,
};

// ── InvestissementBuilder ───────────────────────────────────────────

function InvestissementBuilder({ items, onAdd, onRemove }) {
  const [label, setLabel] = useState("");
  const [montant, setMontant] = useState("");
  return (
    <div>
      <div
        style={{ padding: "10px 12px", background: "#f8fafd", borderRadius: 7 }}
      >
        {items.length === 0 && (
          <div style={{ fontSize: 11, color: "#aaa", fontStyle: "italic" }}>
            Aucun investissement initial.
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 0",
              borderBottom: "1px solid #f0f4f8",
              fontSize: 12,
            }}
          >
            <span style={{ flex: 1, color: "#555" }}>{it.label}</span>
            <span style={{ color: DS.navy, fontWeight: 700 }}>
              {Number(it.montant).toFixed(2)} €
            </span>
            <button
              onClick={() => onRemove(it.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: DS.red,
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Libellé"
            style={{ ...inpStyle, flex: 1 }}
          />
          <input
            type="number"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            placeholder="€"
            style={{ ...inpStyle, width: 90 }}
          />
          <button
            onClick={() => {
              if (label && montant) {
                onAdd(label, montant);
                setLabel("");
                setMontant("");
              }
            }}
            style={{
              padding: "5px 12px",
              background: DS.navy,
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}
